import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Dashboard from '../pages/Dashboard/dashboard';
import FirstOnboarding from '../pages/Onboarding/onboarding';
import ProjectsTable from '../pages/Projects/projectsTable';
import ProjectDetails from '../pages/Projects/projectDetails';
import CreateProject from '../pages/Projects/createProject';
import UserProfile from '../pages/UserProfile/profile';
import SignIn from '../pages/Registration/signin';
import SignUp from '../pages/Registration/signup';
import Datasets from '../pages/Datasets/datasets';
import CreateDatasets from '../pages/Datasets/createDataSet';
import DatasetDetails from '../pages/Datasets/datasetDetails';
import ArticlesTable from '../pages/Articles/articlesTable';
import Articles from '../pages/Articles/CreateArticle';
import ArticleDraft from '../pages/Articles/Article';
import Pinecone from '../pages/DataStorage/DataStorageManager';
import AssistantAI from '../pages/Assistant/assistant_ai';
import ChatDashboard from '../pages/Assistant/ChatDashboard';
import ConversationsPage from '../pages/Assistant/ConversationsPage';
import WidgetDetails, { CreateWidgetForm } from '../pages/Widgets/WidgetDetails';
import AIAgentsLayout from '../pages/Widgets/AIAgentsLayout';
import WebsiteSourcesPage from '../pages/Widgets/WebsiteSourcesPage';
import CRMTable from '../pages/CRM/CRMTable';
import LeadDetails from '../pages/CRM/LeadDetails';
import NotificationsPage from '../pages/Notifications/NotificationsPage';
import PodcastsListPage from '../pages/Podcasts/PodcastsListPage';
import PodcastCreatePage from '../pages/Podcasts/PodcastCreatePage';
import PodcastDetailPage from '../pages/Podcasts/PodcastDetailPage';
import SubscriptionSuccess from '../pages/UserProfile/SubscriptionSuccess';
import AppSelectorPage from '../pages/AppSelector/AppSelectorPage';
import { ChatDetailsPanel } from '../features/sentiment';

// Super admin analytics: own chunk (React.lazy), outside the writer/agent
// modes and reachable with an expired trial. Access is checked inside
// (RequireSuperAdmin), so non-admins end up on `/`.
const AdminRoutes = lazy(() => import('../pages/Admin/AdminRoutes'));
// Lent to the admin chunk instead of imported there: importing it from the
// lazy chunk costs ~18 kB gzip in the main bundle (AdminHostContext.js).
const ADMIN_HOST_COMPONENTS = { ChatDetailsPanel };

const adminRouteElement = (
  <Suspense
    fallback={(
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-500" />
      </div>
    )}
  >
    <AdminRoutes hostComponents={ADMIN_HOST_COMPONENTS} />
  </Suspense>
);

function LegacyModeRedirect({ mode, basePath, withAppModePath }) {
  const location = useLocation();
  const suffix = location.pathname.startsWith(basePath)
    ? location.pathname.slice(basePath.length)
    : '';
  return (
    <Navigate
      to={`${withAppModePath(mode, basePath)}${suffix}${location.search}${location.hash}`}
      replace
    />
  );
}

export default function AppRoutes({
  trialEnded,
  userFirstName,
  handleSignIn,
  activeAppMode,
  withAppModePath
}) {
  return (
    <Routes>
      {trialEnded ? (
        <>
          <Route path="/" element={<AppSelectorPage firstName={userFirstName} trialEnded />} />
          <Route path="/writer" element={<Navigate to="/writer/dashboard" replace />} />
          <Route path="/writer/dashboard" element={<Dashboard />} />
          <Route path="/dashboard" element={<Navigate to="/writer/dashboard" replace />} />
          <Route path="/pricing" element={<Navigate to={{ pathname: '/profile', hash: '#subscription' }} replace />} />
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/subscription-success" element={<SubscriptionSuccess />} />
          <Route path="/admin/*" element={adminRouteElement} />
          <Route path="*" element={<Navigate to="/pricing" replace />} />
        </>
      ) : (
        <>
          <Route path="/signin" element={<SignIn onSignIn={handleSignIn} />} />
          <Route path="/" element={<AppSelectorPage firstName={userFirstName} trialEnded={trialEnded} />} />
          <Route path="/writer" element={<Navigate to="/writer/dashboard" replace />} />
          <Route path="/agent" element={<Navigate to="/agent/assistants" replace />} />
          <Route path="/admin/*" element={adminRouteElement} />

          <Route path="/writer/dashboard" element={<Dashboard />} />
          <Route path="/writer/onboarding" element={<FirstOnboarding />} />
          <Route path="/writer/projects" element={<ProjectsTable />} />
          <Route path="/writer/projects/:id" element={<ProjectDetails />} />
          <Route path="/writer/projects/create-project" element={<CreateProject />} />
          <Route path="/writer/datasets" element={<Datasets />} />
          <Route path="/writer/datasets/create-dataset" element={<CreateDatasets />} />
          <Route path="/writer/datasets/create-dataset/:sourceType" element={<CreateDatasets />} />
          <Route path="/writer/datasets/details/:id" element={<DatasetDetails />} />
          <Route path="/writer/articles/create-article" element={<Navigate to="/writer/articles/create-article/step/setup" replace />} />
          <Route path="/writer/articles/create-article/step/:stepNumber" element={<Articles />} />
          <Route path="/writer/articles/generate-draft" element={<ArticleDraft />} />
          <Route path="/writer/articles" element={<ArticlesTable />} />
          <Route path="/writer/articles/:id" element={<ArticleDraft />} />
          <Route path="/writer/data-storage" element={<Pinecone />} />
          <Route path="/writer/notifications" element={<NotificationsPage />} />
          <Route path="/writer/podcasts" element={<PodcastsListPage />} />
          <Route path="/writer/podcasts/create" element={<PodcastCreatePage />} />
          <Route path="/writer/podcasts/:id" element={<PodcastDetailPage />} />

          <Route path="/agent/dashboard" element={<Dashboard />} />
          <Route path="/agent/onboarding" element={<FirstOnboarding />} />
          <Route path="/agent/assistants" element={<ChatDashboard />} />
          <Route path="/agent/chat/:sessionId" element={<ChatDashboard />} />
          <Route path="/agent/chat" element={<ChatDashboard />} />
          <Route path="/agent/conversations" element={<ConversationsPage />} />
          <Route path="/agent/conversations/:sessionId" element={<ConversationsPage />} />
          <Route path="/agent/ai-assistant" element={<AssistantAI />} />
          <Route path="/agent/ai-agents" element={<AIAgentsLayout />}>
            <Route index element={<Navigate to="create-widget" replace />} />
            <Route path="create-widget" element={<CreateWidgetForm />} />
            <Route path="website-sources" element={<WebsiteSourcesPage />} />
            <Route path="leads" element={<CRMTable />} />
            <Route path="leads/:id" element={<LeadDetails />} />
            <Route path=":id" element={<WidgetDetails />} />
          </Route>
          <Route path="/agent/crm" element={<Navigate to="/agent/ai-agents/leads" replace />} />
          <Route path="/agent/crm/:id" element={<LeadDetails />} />
          <Route path="/agent/datasets" element={<Datasets />} />
          <Route path="/agent/datasets/create-dataset" element={<CreateDatasets />} />
          <Route path="/agent/datasets/create-dataset/:sourceType" element={<CreateDatasets />} />
          <Route path="/agent/datasets/details/:id" element={<DatasetDetails />} />
          <Route path="/agent/notifications" element={<NotificationsPage />} />

          <Route path="/profile" element={<UserProfile />} />
          <Route path="/subscription-success" element={<SubscriptionSuccess />} />
          <Route path="/pricing" element={<Navigate to={{ pathname: '/profile', hash: '#subscription' }} replace />} />
          <Route path="/signup" element={<SignUp />} />

          <Route path="/dashboard" element={<Navigate to="/writer/dashboard" replace />} />
          <Route path="/onboarding" element={<Navigate to="/writer/onboarding" replace />} />
          <Route path="/projects/*" element={<LegacyModeRedirect mode="writer" basePath="/projects" withAppModePath={withAppModePath} />} />
          <Route path="/datasets/*" element={<LegacyModeRedirect mode="writer" basePath="/datasets" withAppModePath={withAppModePath} />} />
          <Route path="/articles/*" element={<LegacyModeRedirect mode="writer" basePath="/articles" withAppModePath={withAppModePath} />} />
          <Route path="/podcasts/*" element={<LegacyModeRedirect mode="writer" basePath="/podcasts" withAppModePath={withAppModePath} />} />
          <Route path="/data-storage" element={<Navigate to="/writer/data-storage" replace />} />
          <Route path="/assistants/*" element={<LegacyModeRedirect mode="agent" basePath="/assistants" withAppModePath={withAppModePath} />} />
          <Route path="/ai-assistant" element={<Navigate to="/agent/ai-assistant" replace />} />
          <Route path="/chat/*" element={<LegacyModeRedirect mode="agent" basePath="/chat" withAppModePath={withAppModePath} />} />
          <Route path="/conversations/*" element={<LegacyModeRedirect mode="agent" basePath="/conversations" withAppModePath={withAppModePath} />} />
          <Route path="/ai-agents/*" element={<LegacyModeRedirect mode="agent" basePath="/ai-agents" withAppModePath={withAppModePath} />} />
          <Route path="/crm/*" element={<LegacyModeRedirect mode="agent" basePath="/crm" withAppModePath={withAppModePath} />} />
          <Route path="/notifications" element={<Navigate to={`/${activeAppMode}/notifications`} replace />} />
        </>
      )}
    </Routes>
  );
}
